/**
 * Reise-copilot: sky-lagring + deling av turer (Fase 3). Erstatter localStorage som
 * kilde for lagrede turer (localStorage beholdes for uferdig utkast-samtale).
 *
 *   users/{uid}/copilot_trips/{tripId}   — private lagrede turer (eier-only)
 *   shared_copilot_trips/{shareId}       — offentlig lesbar delelenke
 */

import { collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy, getDoc } from 'firebase/firestore';
import { db } from './firebaseService';
import { TripSpec, CopilotMessage } from '../types';
import { APP_URL } from '../constants';

export interface SavedCopilotTrip {
  id: string;
  title: string;
  tripSpec: TripSpec;
  messages: CopilotMessage[];
  createdAt: number;
  updatedAt: number;
}

/** Utled en lesbar tittel fra spec hvis brukeren ikke ga en. Ren funksjon. */
export function defaultTripTitle(spec: TripSpec): string {
  const from = (spec.resolvedOriginName || spec.origin || '').split(',')[0].trim();
  const to = (spec.resolvedDestName || spec.destination || '').split(',')[0].trim();
  if (from && to) return `${from} → ${to}`;
  if (to) return `Tur til ${to}`;
  return 'Uten navn';
}

/** Bygg delelenke fra shareId. Ren funksjon. */
export function tripShareUrl(shareId: string): string {
  return `${APP_URL}/?copilotTrip=${encodeURIComponent(shareId)}`;
}

/** Serialiser til Firestore-doc (dropper undefined; Firestore avviser undefined). Ren. */
export function tripToRecord(trip: {
  title?: string; tripSpec: TripSpec; messages: CopilotMessage[]; createdAt?: number;
}, now: number): Omit<SavedCopilotTrip, 'id'> {
  return {
    title: (trip.title && trip.title.trim()) || defaultTripTitle(trip.tripSpec),
    tripSpec: JSON.parse(JSON.stringify(trip.tripSpec)), // dropper undefined-felt
    messages: trip.messages || [],
    createdAt: trip.createdAt ?? now,
    updatedAt: now,
  };
}

/**
 * Lagre (opprett/oppdater) en tur. Returnerer tripId. `id` utelates ved ny tur.
 * `now` injiseres (Date.now() i kaller) for testbarhet.
 */
export async function saveTrip(
  uid: string,
  trip: { id?: string; title?: string; tripSpec: TripSpec; messages: CopilotMessage[]; createdAt?: number },
  now: number,
): Promise<string> {
  const col = collection(db, 'users', uid, 'copilot_trips');
  const ref = trip.id ? doc(col, trip.id) : doc(col);
  await setDoc(ref, tripToRecord(trip, now), { merge: true });
  return ref.id;
}

/** Sanntids-abonnement på brukerens lagrede turer (nyeste først). */
export function subscribeToTrips(uid: string, cb: (trips: SavedCopilotTrip[]) => void): () => void {
  const col = collection(db, 'users', uid, 'copilot_trips');
  const q = query(col, orderBy('updatedAt', 'desc'));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SavedCopilotTrip, 'id'>) })));
  }, () => cb([]));
}

export async function deleteTrip(uid: string, tripId: string): Promise<void> {
  await deleteDoc(doc(db, 'users', uid, 'copilot_trips', tripId));
}

/** Del en tur offentlig. Returnerer delelenke. `now` injiseres for testbarhet. */
export async function shareTrip(
  uid: string,
  trip: { title?: string; tripSpec: TripSpec },
  now: number,
): Promise<string> {
  const ref = doc(collection(db, 'shared_copilot_trips'));
  await setDoc(ref, {
    createdBy: uid,
    isLinkShareEnabled: true,
    title: (trip.title && trip.title.trim()) || defaultTripTitle(trip.tripSpec),
    tripSpec: JSON.parse(JSON.stringify(trip.tripSpec)),
    createdAt: now,
  });
  return tripShareUrl(ref.id);
}

/** Hent en delt tur (offentlig). null hvis den mangler. */
export async function loadSharedTrip(shareId: string): Promise<{ title: string; tripSpec: TripSpec } | null> {
  try {
    const snap = await getDoc(doc(db, 'shared_copilot_trips', shareId));
    if (!snap.exists()) return null;
    const d = snap.data() as any;
    if (!d?.tripSpec) return null;
    return { title: d.title || defaultTripTitle(d.tripSpec), tripSpec: d.tripSpec };
  } catch { return null; }
}
